import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { api } from '../../lib/api';
import { TableScroll } from '../../components/TableScroll';

// KI-Prompt-Cache-Übersicht (Testzeitraum). Aggregiert TutorUsage über alle
// User: Cache-Hit-Ratio, Kosten, geschätzte Cache-Ersparnis, Ø-Kosten/Antwort.
// Backend: GET /admin/health/ai-cache (T1, docs/features/tutor-text-budget.md).

interface ModelRow {
  modelId: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadInputTokens: number;
  cacheCreationInputTokens: number;
  cacheHitRatio: number;
  costEur: number;
  estSavingsEur: number;
  avgCostPerCallEur: number;
}
interface SourceRow {
  source: string;
  calls: number;
  costEur: number;
  cacheHitRatio: number;
}
interface DailyRow {
  date: string;
  calls: number;
  costEur: number;
  inputTokens: number;
  cacheReadInputTokens: number;
}
interface CacheOverview {
  windowDays: number;
  generatedAt: string;
  overall: {
    calls: number;
    inputTokens: number;
    outputTokens: number;
    cacheReadInputTokens: number;
    cacheCreationInputTokens: number;
    cacheHitRatio: number;
    costEur: number;
    estSavingsEur: number;
    avgCostPerCallEur: number;
  };
  byModel: ModelRow[];
  bySource: SourceRow[];
  daily: DailyRow[];
}

const WINDOWS = [7, 30, 90] as const;

function fetchCache(days: number): Promise<CacheOverview> {
  return api.get<CacheOverview>(`/admin/health/ai-cache?days=${days}`);
}

const eur = (n: number) => `€${n.toFixed(n < 10 ? 3 : 2)}`;
const pct = (n: number) => `${Math.round(n * 100)}%`;
const compact = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
const shortModel = (id: string) =>
  id.includes('sonnet') ? 'Sonnet' : id.includes('haiku') ? 'Haiku' : id;

export function CacheInsightsSection(): JSX.Element {
  const [days, setDays] = useState<number>(30);
  const { data, isLoading, error } = useQuery({
    queryKey: ['health', 'ai-cache', days],
    queryFn: () => fetchCache(days),
    refetchInterval: 10 * 60_000,
  });

  const card = {
    background: darkTheme.colors.bgSurface,
    borderColor: darkTheme.colors.borderSubtle,
  };

  if (error) {
    return (
      <div
        className="rounded-md p-3 text-sm"
        style={{ background: darkTheme.colors.dangerBg, color: darkTheme.colors.danger }}
      >
        {error instanceof Error ? error.message : 'Fehler beim Laden der Cache-Daten'}
      </div>
    );
  }

  return (
    <div className="rounded-md border p-4" style={card}>
      <header className="mb-4 flex items-center justify-between">
        <div className="flex gap-1">
          {WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setDays(w)}
              className="rounded px-2.5 py-1 text-xs font-medium"
              style={{
                background: w === days ? darkTheme.colors.accentSubtle : 'transparent',
                color: w === days ? darkTheme.colors.accentLight : darkTheme.colors.textSecondary,
                border: `1px solid ${w === days ? darkTheme.colors.accent : darkTheme.colors.borderSubtle}`,
              }}
            >
              {w} Tage
            </button>
          ))}
        </div>
        {data && (
          <span className="text-[11px] text-text-tertiary">
            {data.overall.calls} Antworten · Stand {new Date(data.generatedAt).toLocaleTimeString('de-DE')}
          </span>
        )}
      </header>

      {isLoading || !data ? (
        <div className="py-6 text-sm text-text-tertiary">Lädt …</div>
      ) : data.overall.calls === 0 ? (
        <div className="py-6 text-sm text-text-tertiary">
          Noch keine KI-Antworten im Zeitfenster. Sobald im Tutor geschrieben wird, erscheinen hier
          Cache-Trefferquote und Kosten.
        </div>
      ) : (
        <>
          {/* Headline-Kennzahlen */}
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Metric
              label="Cache-Trefferquote"
              value={pct(data.overall.cacheHitRatio)}
              hint={`${compact(data.overall.cacheReadInputTokens)} von ${compact(
                data.overall.inputTokens + data.overall.cacheReadInputTokens,
              )} Input-Tokens`}
              accent
            />
            <Metric label="Kosten gesamt" value={eur(data.overall.costEur)} hint={`${days} Tage`} />
            <Metric
              label="Cache-Ersparnis"
              value={eur(data.overall.estSavingsEur)}
              hint="ggü. ohne Cache"
              good
            />
            <Metric
              label="Ø Kosten/Antwort"
              value={eur(data.overall.avgCostPerCallEur)}
              hint="für Budget-Kalibrierung"
            />
          </div>

          {/* Pro Modell */}
          <Table
            cols={['Modell', 'Antw.', 'Cache', 'Kosten', 'Ø/Antw.', 'Ersparnis']}
            rows={data.byModel.map((m) => [
              shortModel(m.modelId),
              String(m.calls),
              pct(m.cacheHitRatio),
              eur(m.costEur),
              eur(m.avgCostPerCallEur),
              eur(m.estSavingsEur),
            ])}
          />

          {/* Pro Source (nur wenn mehr als tutor) */}
          {data.bySource.length > 1 && (
            <div className="mt-3">
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
                Nach Quelle
              </p>
              <Table
                cols={['Quelle', 'Antw.', 'Cache', 'Kosten']}
                rows={data.bySource.map((s) => [
                  s.source,
                  String(s.calls),
                  pct(s.cacheHitRatio),
                  eur(s.costEur),
                ])}
              />
            </div>
          )}

          {/* Mini-Tagesverlauf (nur Tage mit Aktivität) */}
          {(() => {
            const active = data.daily.filter((d) => d.calls > 0).slice(-10);
            if (active.length === 0) return null;
            return (
              <div className="mt-3">
                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
                  Letzte aktive Tage
                </p>
                <Table
                  cols={['Tag', 'Antw.', 'Cache', 'Kosten']}
                  rows={active.map((d) => [
                    d.date.slice(5),
                    String(d.calls),
                    pct(d.inputTokens + d.cacheReadInputTokens > 0 ? d.cacheReadInputTokens / (d.inputTokens + d.cacheReadInputTokens) : 0),
                    eur(d.costEur),
                  ])}
                />
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  hint,
  accent,
  good,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: boolean;
  good?: boolean;
}): JSX.Element {
  const color = good
    ? darkTheme.colors.success
    : accent
      ? darkTheme.colors.accentLight
      : darkTheme.colors.textPrimary;
  return (
    <div
      className="rounded-md border p-3"
      style={{ background: darkTheme.colors.bgElevated, borderColor: darkTheme.colors.borderSubtle }}
    >
      <p className="text-[11px] text-text-tertiary">{label}</p>
      <p className="mt-0.5 text-xl font-bold" style={{ color }}>
        {value}
      </p>
      {hint && <p className="mt-0.5 text-[10px] text-text-tertiary">{hint}</p>}
    </div>
  );
}

function Table({ cols, rows }: { cols: string[]; rows: string[][] }): JSX.Element {
  // Breite Tabellen (>= 5 Spalten, z.B. die Modell-Übersicht) scrollen auf
  // schmalen Viewports horizontal; schmale (2-4 Spalten) passen und bleiben w-full.
  const wide = cols.length >= 5;
  return (
    <TableScroll className="" minWidthClass={wide ? 'min-w-[560px]' : 'min-w-0'}>
      <table className="w-full text-xs">
      <thead>
        <tr style={{ color: darkTheme.colors.textTertiary }}>
          {cols.map((c, i) => (
            <th key={c} className={`pb-1.5 font-medium ${i === 0 ? 'text-left' : 'text-right'}`}>
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, ri) => (
          <tr key={ri} style={{ borderTop: `1px solid ${darkTheme.colors.borderSubtle}` }}>
            {r.map((cell, ci) => (
              <td
                key={ci}
                className={`py-1.5 ${ci === 0 ? 'text-left font-medium text-text-primary' : 'text-right text-text-secondary'}`}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
      </table>
    </TableScroll>
  );
}
